import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  Save, Eye, ArrowUp, ArrowDown, Plus, Trash2, Lock, Loader2, RefreshCw, Info,
} from 'lucide-react';

/**
 * InstantReportScriptsEditor
 *
 * One-stop admin editor for the Instant Roof Report (the free / auto-
 * generated PDF sent right after a phone-scoped appraisal).
 *
 * For each of the 6 roof-type templates stored in `estimate_templates`, an
 * operator can:
 *   • edit every editable script (title, intro, repair options bullets,
 *     ridging info, restoration description, vent note, discount note),
 *   • reorder the sections that appear in the PDF (drag-free up/down
 *     arrows — no library needed),
 *   • toggle any section on/off (rep_footer stays locked-on to keep the
 *     legal contact block on every report),
 *   • preview the resulting PDF in a new browser tab using any appraisal
 *     of matching roof type.
 *
 * Section metadata (order, labels, template-field mapping) comes from
 * `GET /api/instant-report-sections` so the labels stay in sync with the
 * PDF generator without duplicating strings on the frontend.
 */
export default function InstantReportScriptsEditor() {
  const { api } = useAuth();
  const location = useLocation();

  const [meta, setMeta] = useState(null);           // /instant-report-sections
  const [templates, setTemplates] = useState([]);   // /estimate-templates
  const [variant, setVariantState] = useState('instant_free'); // 3-way toggle
  const [activeKey, setActiveKey] = useState('');
  const [form, setForm] = useState(null);            // clone of active template
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  // isDirty tracks REAL user mutations — flips true on any onChange, back
  // to false after Save or after switching templates. Fixes the false
  // "unsaved changes" prompt that fired on a fresh page load.
  const [isDirty, setIsDirty] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewAppraisals, setPreviewAppraisals] = useState([]);
  const [previewAppraisalId, setPreviewAppraisalId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [metaRes, tplRes] = await Promise.all([
        api.get('/instant-report-sections'),
        api.get('/estimate-templates'),
      ]);
      setMeta(metaRes.data);
      const sorted = [...tplRes.data].sort((a, b) => (a.roof_key || '').localeCompare(b.roof_key || ''));
      setTemplates(sorted);
      // Templates come back as 18 docs (6 roof_keys × 3 variants). The
      // roof-type picker below is scoped to the currently-active variant.
      const currentVariantTpls = sorted.filter(t => (t.variant || 'detailed_single') === variant);
      if (currentVariantTpls.length && !activeKey) {
        // Honour a deep-link from the Roof Appraisal tab:
        //   /report-setup?tab=instant_report&roof_key=<key>
        //   /report-setup?tab=instant_report&roof_type=<free-text>
        // Falls back to first template if no query hint matches.
        const params = new URLSearchParams(location.search);
        const wantKey = (params.get('roof_key') || '').trim().toLowerCase();
        const wantType = (params.get('roof_type') || '').trim().toLowerCase();
        let chosen = '';
        if (wantKey) {
          chosen = currentVariantTpls.find(t => (t.roof_key || '').toLowerCase() === wantKey)?.roof_key || '';
        }
        if (!chosen && wantType) {
          // Match via `match_terms` (case-insensitive substring both ways) so
          // the free-text roof_type on the appraisal ("LongRun Iron",
          // "Modern Concrete", …) picks the right template without the
          // caller needing to know internal roof_key slugs.
          chosen = currentVariantTpls.find(t => (t.match_terms || []).some(m => {
            const term = (m || '').toLowerCase();
            return term && (term.includes(wantType) || wantType.includes(term));
          }))?.roof_key || '';
        }
        setActiveKey(chosen || currentVariantTpls[0].roof_key);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load templates');
    }
    setLoading(false);
  }, [api, location.search, activeKey]);

  useEffect(() => { load(); }, [load]);

  // Populate the editor `form` whenever the active roof-type OR variant changes.
  useEffect(() => {
    const t = templates.find(x => x.roof_key === activeKey && (x.variant || 'detailed_single') === variant);
    if (!t) { setForm(null); return; }
    setForm({
      ...t,
      main_repair_options: [...(t.main_repair_options || [])],
      section_order: [...(t.section_order || meta?.default_order || [])],
      section_visibility: { ...(t.section_visibility || {}) },
      roof_details_fields: [...(t.roof_details_fields || meta?.default_roof_details_fields || [])],
    });
    setSaved(false);
    setIsDirty(false);   // switching templates resets the dirty flag
  }, [activeKey, variant, templates, meta]);

  // Fetch a shortlist of appraisals matching this roof-type via the
  // backend (which joins appraisals → roof_details to resolve
  // roof_type). Falls back to any-roof-type list so the picker always
  // has options. Refetches whenever the active template changes.
  useEffect(() => {
    if (!api || !activeKey) return;
    (async () => {
      try {
        const { data } = await api.get(`/estimate-templates/${activeKey}/matching-appraisals`, {
          params: { limit: 25 },
        });
        setPreviewAppraisals(data.items || []);
      } catch { /* non-fatal — Preview button surfaces a friendly alert if empty */ }
    })();
  }, [api, activeKey]);

  const roofTypeToKey = useMemo(() => {
    // The template's `match_terms` tell us which roof_type strings map to
    // it. Build a reverse lookup so the preview picker can shortlist
    // appraisals compatible with the currently-edited template.
    const lookup = {};
    for (const t of templates) {
      for (const term of (t.match_terms || [])) {
        lookup[(term || '').toLowerCase().trim()] = t.roof_key;
      }
    }
    return lookup;
  }, [templates]);

  // The backend already server-side filters by roof_type match_terms, so
  // just surface whatever came back. Kept the useMemo shape to minimise
  // churn on the JSX below.
  const matchingAppraisals = useMemo(() => previewAppraisals, [previewAppraisals]);

  const u = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setSaved(false);
    setIsDirty(true);
  };

  // Full delete: clear the text AND hide the corresponding PDF section so
  // the section doesn't render as an empty gap. Uses TEMPLATE_FIELDS
  // reverse-lookup so we know which section key belongs to which text
  // field (the mapping lives on `meta.template_fields`).
  const deleteScript = (fieldName) => {
    if (!fieldName) return;
    if (!window.confirm(`Delete this section from the PDF?\n\nThis will clear the text AND hide "${fieldName.replace(/_/g, ' ')}" from the report. You can restore it later by re-typing content and toggling the section back on in the section-order panel.`)) {
      return;
    }
    // Reverse lookup: find the section key whose template_field matches
    const tf = (meta && meta.template_fields) || {};
    const sectionKey = Object.keys(tf).find(k => tf[k] === fieldName);
    setForm(prev => {
      const next = { ...prev, [fieldName]: '' };
      if (sectionKey) {
        next.section_visibility = {
          ...(prev.section_visibility || {}),
          [sectionKey]: false,
        };
      }
      return next;
    });
    setSaved(false);
    setIsDirty(true);
  };

  const moveSection = (idx, dir) => {
    setForm(prev => {
      const order = [...prev.section_order];
      const swap = idx + dir;
      if (swap < 0 || swap >= order.length) return prev;
      [order[idx], order[swap]] = [order[swap], order[idx]];
      return { ...prev, section_order: order };
    });
    setSaved(false);
    setIsDirty(true);
  };

  const toggleVisible = (key) => {
    setForm(prev => ({
      ...prev,
      section_visibility: {
        ...prev.section_visibility,
        [key]: prev.section_visibility[key] === false ? true : false,
      },
    }));
    setSaved(false);
    setIsDirty(true);
  };

  const addOption = () => {
    setForm(prev => ({ ...prev, main_repair_options: [...(prev.main_repair_options || []), ''] }));
    setIsDirty(true);
  };
  const removeOption = (i) => {
    setForm(prev => ({ ...prev, main_repair_options: prev.main_repair_options.filter((_, k) => k !== i) }));
    setIsDirty(true);
  };
  const updateOption = (i, v) => {
    setForm(prev => ({
      ...prev,
      main_repair_options: prev.main_repair_options.map((x, k) => (k === i ? v : x)),
    }));
    setSaved(false);
    setIsDirty(true);
  };
  const moveOption = (i, dir) => {
    setForm(prev => {
      const arr = [...prev.main_repair_options];
      const swap = i + dir;
      if (swap < 0 || swap >= arr.length) return prev;
      [arr[i], arr[swap]] = [arr[swap], arr[i]];
      return { ...prev, main_repair_options: arr };
    });
    setSaved(false);
    setIsDirty(true);
  };

  const save = async () => {
    if (!form) return;
    setSaving(true);
    try {
      // Strip any noisy client-side fields before PUT
      const body = { ...form };
      delete body._id;
      const { data } = await api.put(`/estimate-templates/${form.roof_key}`, body, {
        params: { variant },
      });
      // Replace the row in the templates list so the picker stays in sync.
      // Match on BOTH roof_key + variant so we only touch the row we saved.
      setTemplates(prev => prev.map(t => (
        t.roof_key === form.roof_key && (t.variant || 'detailed_single') === variant
          ? { ...t, ...data }
          : t
      )));
      setSaved(true);
      setIsDirty(false);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      alert('Save failed: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const preview = async () => {
    const id = previewAppraisalId || matchingAppraisals[0]?.id || matchingAppraisals[0]?._id;
    if (!id) {
      alert('No matching appraisal to preview. Pick an appraisal id in the "Preview using" box.');
      return;
    }
    setPreviewing(true);
    let blobUrl = null;
    try {
      // Warn (but do not block) if the user has REAL unsaved edits. A
      // freshly-loaded editor is not "dirty" any more, so this dialog
      // no longer fires on the very first click after page load.
      if (isDirty && form) {
        const cont = window.confirm('You have unsaved changes. Save first so the preview reflects them?');
        if (cont) await save();
      }
      const res = await api.get(
        variant === 'instant_free'
          ? `/reports/instant-roof-report/${id}`
          : `/reports/detailed-roof-appraisal/${id}`,
        { responseType: 'blob', params: variant === 'instant_free' ? {} : { variant } },
      );
      blobUrl = URL.createObjectURL(res.data);
      const win = window.open(blobUrl, '_blank', 'noopener,noreferrer');
      if (!win || win.closed || typeof win.closed === 'undefined') {
        alert('Preview blocked by your browser. Please allow pop-ups for this site and try again.');
        URL.revokeObjectURL(blobUrl);
        return;
      }
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (e) {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
      alert('Preview failed: ' + (e?.response?.data?.detail || e.message || 'unknown error'));
    } finally {
      setPreviewing(false);
    }
  };

  if (loading) return <div className="text-center py-12 text-slate-400"><Loader2 className="animate-spin inline-block" size={16} /> Loading Instant Report templates…</div>;
  if (error) return <div className="p-4 bg-red-50 border border-red-200 rounded text-sm text-red-800">{error}</div>;
  if (!form || !meta) return <div className="text-sm text-slate-500">No templates found.</div>;

  const labels = meta.labels || {};
  const templateFieldMap = meta.template_fields || {};
  // Any known section keys the current order is missing get appended so
  // toggling still works for new sections until admin reorders.
  const orderedKeys = [
    ...form.section_order,
    ...meta.default_order.filter(k => !form.section_order.includes(k)),
  ];

  // Restrict the roof-type picker to templates belonging to the current
  // variant (Instant Free / Detailed 1-Comment / Detailed Multi-Defect).
  const variantTemplates = templates.filter(t => (t.variant || 'detailed_single') === variant);

  const VARIANT_TABS = [
    { key: 'instant_free',    label: 'Instant Free',        color: 'bg-slate-500',  accent: 'text-slate-700',  badge: 'bg-slate-100 text-slate-700' },
    { key: 'detailed_single', label: 'Detailed 1-Comment',  color: 'bg-amber-500',  accent: 'text-amber-700',  badge: 'bg-amber-100 text-amber-800' },
    { key: 'detailed_multi',  label: 'Detailed Multi-Defect', color: 'bg-purple-500', accent: 'text-purple-700', badge: 'bg-purple-100 text-purple-800' },
  ];

  const setVariant = (v) => {
    if (v === variant) return;
    if (isDirty) {
      const cont = window.confirm('You have unsaved changes on the current variant. Discard them and switch?');
      if (!cont) return;
    }
    setVariantState(v);
    setIsDirty(false);
    setSaved(false);
    // Reset activeKey so the effect below re-selects a template for the new variant.
    const firstForVariant = templates.find(t => (t.variant || 'detailed_single') === v && t.roof_key === activeKey);
    if (!firstForVariant) {
      const anyForVariant = templates.find(t => (t.variant || 'detailed_single') === v);
      setActiveKey(anyForVariant?.roof_key || '');
    }
  };

  const activeVariantMeta = VARIANT_TABS.find(v => v.key === variant) || VARIANT_TABS[0];

  return (
    <div data-testid="instant-report-scripts-editor" className="space-y-5">
      {/* Report Variant selector — 3-way toggle so an admin can edit
          section order + scripts independently for Instant Free, Detailed
          1-Comment, and Detailed Multi-Defect PDFs. Each variant is saved
          to its own estimate_templates document (keyed by roof_key +
          variant). */}
      <div className="flex flex-wrap items-center gap-3 bg-gradient-to-r from-slate-50 to-white border border-[#E2E2DF] rounded-lg p-3">
        <span className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Report variant</span>
        <div data-testid="report-variant-toggle" className="flex gap-1 bg-slate-100 rounded-md p-1">
          {VARIANT_TABS.map(v => {
            const isActive = v.key === variant;
            return (
              <button
                key={v.key}
                data-testid={`report-variant-tab-${v.key}`}
                onClick={() => setVariant(v.key)}
                className={`px-3 py-1.5 rounded text-xs font-semibold transition-colors ${isActive ? `${v.color} text-white shadow-sm` : 'text-slate-600 hover:text-slate-900'}`}
                title={`Edit the ${v.label} report's scripts and section order`}
              >
                {v.label}
              </button>
            );
          })}
        </div>
        <span className={`text-[10px] font-mono px-2 py-0.5 rounded ${activeVariantMeta.badge}`} title="Currently editing this variant">
          editing · {activeVariantMeta.label}
        </span>
        <span className="text-[10px] text-slate-400 italic">
          Scripts + section order are stored independently per variant.
        </span>
      </div>

      {/* Roof-type picker */}
      <div className="flex flex-wrap items-center gap-2 border-b border-[#E2E2DF] pb-3">
        <span className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mr-2">Roof type</span>
        {variantTemplates.map(t => (
          <button
            key={t.roof_key}
            data-testid={`roof-type-tab-${t.roof_key}`}
            onClick={() => setActiveKey(t.roof_key)}
            className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${activeKey === t.roof_key ? 'bg-[#316536] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
          >
            {t.title || t.roof_key}
          </button>
        ))}
        <button
          data-testid="reload-templates"
          onClick={load}
          title="Reload from server"
          className="ml-2 p-1.5 text-slate-400 hover:text-[#D35400]"
        >
          <RefreshCw size={13} />
        </button>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
        {/* LEFT — editable script fields (2 columns wide) */}
        <div className="xl:col-span-2 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold text-slate-900 font-[Manrope]">
              Script content — {form.title || form.roof_key}
              <span className={`ml-2 text-[10px] font-mono px-1.5 py-0.5 rounded ${activeVariantMeta.badge}`}>{activeVariantMeta.label}</span>
            </h3>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1.5">
                <label className="text-[11px] text-slate-500 uppercase tracking-wider font-bold">Preview using</label>
                <select
                  data-testid="preview-appraisal-picker"
                  value={previewAppraisalId}
                  onChange={e => setPreviewAppraisalId(e.target.value)}
                  className="px-2 py-1 text-xs border border-[#E2E2DF] rounded-md max-w-[220px]"
                >
                  <option value="">Auto (latest match)</option>
                  {matchingAppraisals.map(a => (
                    <option key={a.id || a._id} value={a.id || a._id}>
                      {a.appraisal_no || (a.id || a._id).slice(-6)}
                      {a.client_name ? ` — ${a.client_name}` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <button
                data-testid="preview-instant-report-btn"
                onClick={preview}
                disabled={previewing}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border border-[#316536] text-[#316536] hover:bg-[#316536] hover:text-white rounded-md disabled:opacity-50 transition-all"
              >
                <Eye size={12} /> {previewing ? 'Opening…' : 'Preview PDF'}
              </button>
              <button
                data-testid="save-instant-report-scripts"
                onClick={save}
                disabled={saving}
                className={`flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold rounded-md transition-all ${saved ? 'bg-green-500 text-white' : 'bg-[#D35400] hover:bg-[#A04000] text-white'} disabled:opacity-50`}
              >
                <Save size={12} /> {saving ? 'Saving…' : saved ? 'Saved!' : 'Save'}
              </button>
            </div>
          </div>

          <FieldGroup label="Report title" onClear={() => u('appraisal_title', '')} onDelete={() => deleteScript('appraisal_title')} testId="tpl-title">
            <input
              data-testid="tpl-appraisal-title"
              value={form.appraisal_title || ''}
              onChange={e => u('appraisal_title', e.target.value)}
              className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm"
            />
          </FieldGroup>

          <FieldGroup label="Intro paragraph" onClear={() => u('intro_appraisal', '')} onDelete={() => deleteScript('intro_appraisal')} hint="Supports {property_address}, {client_name}, {roof_type}">
            <textarea
              data-testid="tpl-intro-appraisal"
              rows={4}
              value={form.intro_appraisal || ''}
              onChange={e => u('intro_appraisal', e.target.value)}
              className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm font-mono"
            />
          </FieldGroup>

          <FieldGroup label="Repair options label" onClear={() => u('main_repair_options_label', '')}>
            <input
              data-testid="tpl-repair-options-label"
              value={form.main_repair_options_label || ''}
              onChange={e => u('main_repair_options_label', e.target.value)}
              className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm"
            />
          </FieldGroup>

          <FieldGroup label="Repair options (bullets)">
            <div className="space-y-1.5">
              {(form.main_repair_options || []).map((opt, i) => (
                <div key={i} className="flex items-start gap-2">
                  <div className="flex flex-col">
                    <button
                      onClick={() => moveOption(i, -1)}
                      disabled={i === 0}
                      className="p-1 text-slate-400 hover:text-[#D35400] disabled:opacity-30"
                      title="Move up"
                    >
                      <ArrowUp size={12} />
                    </button>
                    <button
                      onClick={() => moveOption(i, 1)}
                      disabled={i === form.main_repair_options.length - 1}
                      className="p-1 text-slate-400 hover:text-[#D35400] disabled:opacity-30"
                      title="Move down"
                    >
                      <ArrowDown size={12} />
                    </button>
                  </div>
                  <textarea
                    data-testid={`tpl-option-${i}`}
                    value={opt}
                    onChange={e => updateOption(i, e.target.value)}
                    rows={2}
                    className="flex-1 px-3 py-1.5 border border-[#E2E2DF] rounded-md text-xs"
                  />
                  <button
                    onClick={() => removeOption(i)}
                    className="p-1.5 text-slate-400 hover:text-red-500"
                    title="Remove"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              ))}
              <button
                data-testid="tpl-add-option"
                onClick={addOption}
                className="flex items-center gap-1 text-xs font-semibold text-[#316536] hover:text-[#D35400]"
              >
                <Plus size={12} /> Add option
              </button>
            </div>
          </FieldGroup>

          <FieldGroup label="Roofing information paragraph" onClear={() => u('ridging_info', '')} onDelete={() => deleteScript('ridging_info')}>
            <textarea
              data-testid="tpl-ridging-info"
              rows={4}
              value={form.ridging_info || ''}
              onChange={e => u('ridging_info', e.target.value)}
              className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm font-mono"
            />
          </FieldGroup>

          {/* ---------------- Word-doc script fields ------------------ */}
          <FieldGroup
            onClear={() => u('repair_estimate_comment', '')} onDelete={() => deleteScript('repair_estimate_comment')}
            label="Repair Estimate Comment"
            hint={'Two paragraphs separated by blank line. Supports {profile}, {roof_type}, {rescrew_estimation}, {extra_work_estimate}.'}
          >
            <textarea
              data-testid="tpl-repair-estimate-comment"
              rows={5}
              value={form.repair_estimate_comment || ''}
              onChange={e => u('repair_estimate_comment', e.target.value)}
              className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm font-mono"
            />
          </FieldGroup>

          <FieldGroup
            onClear={() => u('recoat_restoration_comment', '')} onDelete={() => deleteScript('recoat_restoration_comment')}
            label="Recoat Restoration Comment"
            hint={'Two paragraphs separated by blank line. Supports {restoration_estimation}, {roof_type}.'}
          >
            <textarea
              data-testid="tpl-recoat-restoration-comment"
              rows={5}
              value={form.recoat_restoration_comment || ''}
              onChange={e => u('recoat_restoration_comment', e.target.value)}
              className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm font-mono"
            />
          </FieldGroup>

          <FieldGroup label="Restoration Notes" onClear={() => u('restoration_notes', '')} onDelete={() => deleteScript('restoration_notes')} hint="Single paragraph describing the process for this roof type.">
            <textarea
              data-testid="tpl-restoration-notes"
              rows={3}
              value={form.restoration_notes || ''}
              onChange={e => u('restoration_notes', e.target.value)}
              className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm font-mono"
            />
          </FieldGroup>

          <FieldGroup label="Reroof Notes" onClear={() => u('reroof_notes', '')} onDelete={() => deleteScript('reroof_notes')} hint={'Supports {roof_type}, {reroof_estimation}.'}>
            <textarea
              data-testid="tpl-reroof-notes"
              rows={4}
              value={form.reroof_notes || ''}
              onChange={e => u('reroof_notes', e.target.value)}
              className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm font-mono"
            />
          </FieldGroup>

          {/* Roof Details field-order grid — drag-and-drop 3×4 */}
          <FieldGroup
            label="Roof Details grid order (3 × 4)"
            hint="Drag any cell to reorder the 12 Roof Details fields displayed on the PDF."
          >
            <RoofDetailsGridEditor
              fields={form.roof_details_fields || []}
              fieldMeta={meta.roof_detail_fields || {}}
              onChange={arr => u('roof_details_fields', arr)}
            />
          </FieldGroup>

          {/* ---------------- Legacy fields (default hidden) ---------- */}
          <details className="rounded-md border border-slate-200 bg-slate-50/50">
            <summary className="cursor-pointer px-3 py-2 text-xs font-semibold text-slate-500 select-none">
              Legacy fields — deprecated, kept for backwards compatibility
            </summary>
            <div className="p-3 space-y-3">
              <FieldGroup label="Restoration description (legacy)" onClear={() => u('restoration_description', '')} onDelete={() => deleteScript('restoration_description')}>
                <textarea
                  data-testid="tpl-restoration"
                  rows={3}
                  value={form.restoration_description || ''}
                  onChange={e => u('restoration_description', e.target.value)}
                  className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm font-mono"
                />
              </FieldGroup>

              <FieldGroup label="Discount / closing note (legacy)" onClear={() => u('discount_note', '')} onDelete={() => deleteScript('discount_note')}>
                <textarea
                  data-testid="tpl-discount-note"
                  rows={3}
                  value={form.discount_note || ''}
                  onChange={e => u('discount_note', e.target.value)}
                  className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm font-mono"
                />
              </FieldGroup>
            </div>
          </details>

          <FieldGroup label="Vent note (optional)" onClear={() => u('vent_note', '')} onDelete={() => deleteScript('vent_note')}>
            <textarea
              data-testid="tpl-vent-note"
              rows={2}
              value={form.vent_note || ''}
              onChange={e => u('vent_note', e.target.value)}
              className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm font-mono"
            />
          </FieldGroup>

          {/* Illustration controls — the sad-house + truck vignette from the
              legacy Access reports. Controls the size and which "side" the
              truck faces so admins can flip it if a specific quote lays
              out better with the mirrored artwork. */}
          <FieldGroup label="Illustration (house + truck vignette)" hint="Only rendered if the `illustration` section is visible in the section order.">
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Facing</label>
                <select
                  data-testid="tpl-illustration-variant"
                  value={form.illustration_variant || 'right'}
                  onChange={e => u('illustration_variant', e.target.value)}
                  className="px-3 py-1.5 border border-[#E2E2DF] rounded-md text-sm bg-white"
                >
                  <option value="right">Truck on left / house on right</option>
                  <option value="left">Truck on right / house on left</option>
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">
                  Width (mm) — <span className="text-slate-400 font-normal">{Number(form.illustration_width_mm ?? 38).toFixed(0)}mm</span>
                </label>
                <input
                  data-testid="tpl-illustration-width"
                  type="range"
                  min={20}
                  max={65}
                  step={1}
                  value={Number(form.illustration_width_mm ?? 38)}
                  onChange={e => u('illustration_width_mm', Number(e.target.value))}
                  className="w-56"
                />
              </div>
            </div>
          </FieldGroup>
        </div>

        {/* RIGHT — section order + visibility panel */}
        <div className="xl:col-span-1">
          <div className="sticky top-2 space-y-2">
            <div>
              <h3 className="text-sm font-bold text-slate-900 font-[Manrope]">Section order & visibility</h3>
              <p className="text-[11px] text-slate-500 leading-relaxed mt-0.5 mb-2 flex items-start gap-1">
                <Info size={11} className="mt-0.5 flex-shrink-0" />
                <span>Drag with arrows. Toggle the checkbox to hide a section from the PDF. <span className="italic">Rep footer is locked</span>.</span>
              </p>
            </div>
            <div data-testid="section-order-list" className="border border-[#E2E2DF] rounded-md divide-y divide-[#E2E2DF] bg-slate-50">
              {orderedKeys.map((k, idx) => {
                const visible = form.section_visibility[k] !== false;
                const isLocked = k === 'rep_footer';
                const isTemplateDriven = !!templateFieldMap[k];
                return (
                  <div
                    key={k}
                    data-testid={`section-row-${k}`}
                    className={`flex items-center gap-2 px-2 py-1.5 text-xs ${visible ? '' : 'opacity-60'}`}
                  >
                    <span className="text-slate-400 font-mono text-[10px] w-5 text-right">{idx + 1}.</span>
                    <div className="flex flex-col">
                      <button
                        onClick={() => moveSection(idx, -1)}
                        disabled={idx === 0}
                        className="p-0.5 text-slate-400 hover:text-[#D35400] disabled:opacity-20"
                        title="Move up"
                        data-testid={`section-up-${k}`}
                      >
                        <ArrowUp size={11} />
                      </button>
                      <button
                        onClick={() => moveSection(idx, 1)}
                        disabled={idx === orderedKeys.length - 1}
                        className="p-0.5 text-slate-400 hover:text-[#D35400] disabled:opacity-20"
                        title="Move down"
                        data-testid={`section-down-${k}`}
                      >
                        <ArrowDown size={11} />
                      </button>
                    </div>
                    <input
                      type="checkbox"
                      data-testid={`section-visible-${k}`}
                      checked={visible}
                      onChange={() => toggleVisible(k)}
                      disabled={isLocked}
                      className="rounded"
                    />
                    <span className="flex-1 truncate" title={labels[k] || k}>
                      {labels[k] || k}
                    </span>
                    {isLocked && <Lock size={10} className="text-slate-400" />}
                    {isTemplateDriven && !isLocked && (
                      <span className="text-[9px] px-1 py-0 rounded bg-emerald-50 text-emerald-700 font-mono uppercase tracking-wide">script</span>
                    )}
                  </div>
                );
              })}
            </div>
            <p className="text-[10px] text-slate-400 leading-relaxed">
              <b>Script</b> = editable text on the left. Non-script sections pull live data from the appraisal (per-area conditions, roof details, extra works).
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function FieldGroup({ label, hint, testId, children, onClear, onDelete }) {
  return (
    <div data-testid={testId}>
      <div className="flex items-center justify-between mb-1 gap-2">
        <label className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500">{label}</label>
        <div className="flex items-center gap-2">
          {onClear && (
            <button
              type="button"
              onClick={onClear}
              title={`Clear this ${label.toLowerCase()} (empties the text but keeps the section)`}
              className="flex items-center gap-1 text-[10px] font-semibold text-slate-400 hover:text-amber-600 uppercase tracking-wider transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              Clear
            </button>
          )}
          {onDelete && (
            <button
              type="button"
              onClick={onDelete}
              title={`Delete this ${label.toLowerCase()} — empties the text AND hides the section from the PDF`}
              className="flex items-center gap-1 text-[10px] font-semibold text-slate-400 hover:text-red-500 uppercase tracking-wider transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6"></path><path d="M10 11v6"></path><path d="M14 11v6"></path><path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"></path></svg>
              Delete
            </button>
          )}
        </div>
      </div>
      {children}
      {hint && <p className="text-[10px] text-slate-400 mt-1 italic">{hint}</p>}
    </div>
  );
}


/**
 * RoofDetailsGridEditor — drag-and-drop 3×4 grid for reordering the 12
 * Roof Details field cells shown on the Instant Roof Report PDF.
 *
 * Exported so the Script Audit Studio can reuse the exact same widget
 * (single source of truth for grid reordering across the app).
 *
 * Uses the native HTML5 drag-and-drop API (no external library). Each
 * cell can be dragged onto any other cell; dropping swaps their
 * positions. This keeps the interaction dead-simple — grab a cell,
 * drop it where you want it — matching the mental model of a
 * physical grid rearrangement.
 */
export function RoofDetailsGridEditor({ fields, fieldMeta, onChange }) {
  const [draggingIdx, setDraggingIdx] = React.useState(null);
  const [dragOverIdx, setDragOverIdx] = React.useState(null);

  // Fall back to a stable order if the template hasn't been seeded yet.
  const list = React.useMemo(() => {
    const arr = Array.isArray(fields) && fields.length ? [...fields] : [];
    // Pad to exactly 12 cells so the grid always has the same shape.
    while (arr.length < 12) arr.push('');
    return arr.slice(0, 12);
  }, [fields]);

  const doSwap = (from, to) => {
    if (from === to || from == null || to == null) return;
    const next = [...list];
    [next[from], next[to]] = [next[to], next[from]];
    onChange(next);
  };

  const onDragStart = (e, idx) => {
    setDraggingIdx(idx);
    e.dataTransfer.effectAllowed = 'move';
    // Firefox requires setting data for drag to fire.
    e.dataTransfer.setData('text/plain', String(idx));
  };
  const onDragOver = (e, idx) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverIdx !== idx) setDragOverIdx(idx);
  };
  const onDrop = (e, idx) => {
    e.preventDefault();
    doSwap(draggingIdx, idx);
    setDraggingIdx(null);
    setDragOverIdx(null);
  };
  const onDragEnd = () => {
    setDraggingIdx(null);
    setDragOverIdx(null);
  };

  const cellClass = (i) => {
    const base =
      'flex items-center justify-between gap-1 px-2 py-2 border rounded-md text-xs cursor-grab active:cursor-grabbing select-none transition-colors';
    if (draggingIdx === i) return `${base} bg-[#D35400] text-white border-[#D35400] opacity-70`;
    if (dragOverIdx === i && draggingIdx != null && draggingIdx !== i)
      return `${base} bg-emerald-100 border-[#316536] border-dashed`;
    return `${base} bg-white border-slate-200 hover:border-slate-300`;
  };

  return (
    <div>
      <div data-testid="roof-details-grid-editor" className="grid grid-cols-3 gap-1.5">
        {list.map((key, i) => {
          const label = key && fieldMeta[key]?.label ? fieldMeta[key].label : (key || '(empty)');
          return (
            <div
              key={`${key}-${i}`}
              data-testid={`roof-details-cell-${i}`}
              data-field-key={key}
              draggable
              onDragStart={(e) => onDragStart(e, i)}
              onDragOver={(e) => onDragOver(e, i)}
              onDrop={(e) => onDrop(e, i)}
              onDragEnd={onDragEnd}
              className={cellClass(i)}
              title={`Cell ${i + 1} — drag to swap with another cell`}
            >
              <span className="font-mono text-[9px] text-slate-400 shrink-0">{i + 1}</span>
              <span className="flex-1 text-center font-semibold truncate">{label}</span>
              <span className="w-3" />{/* balance spacer */}
            </div>
          );
        })}
      </div>
      <p className="text-[10px] text-slate-400 mt-1.5 italic">
        Cells 1-3 = row 1 · 4-6 = row 2 · 7-9 = row 3 · 10-12 = row 4.
      </p>
    </div>
  );
}
